import { useState, useEffect, useCallback } from "react";
import axios from "../axios";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { Flash, Modal, FormField, ModalActions, Pagination } from "../components/AdminUI";
import "../styles/admin.css";

export default function AdminStartsPage() {
  const [starts, setStarts]   = useState([]);
  const [depots, setDepots]   = useState([]);
  const [routes, setRoutes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [flash, setFlash]     = useState({ message: "", type: "success" });

  // フィルター
  const [depotFilter, setDepotFilter] = useState("");
  const [routeFilter, setRouteFilter] = useState("");
  const [searchQ, setSearchQ]         = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const PER_PAGE = 20;

  // 追加・編集モーダル
  const [modalOpen, setModalOpen] = useState(false);
  const [editStart, setEditStart] = useState(null);
  const [form, setForm] = useState({
    depot_id: "", route_id: "", short_name: "", full_name: "", sort_number: "",
  });
  const [saving, setSaving] = useState(false);

  // ── データ取得 ──
  const loadDepots = useCallback(async () => {
    try {
      const res = await axios.get("/depots");
      setDepots(Array.isArray(res.data) ? res.data : []);
    } catch { setDepots([]); }
  }, []);

  const loadRoutes = useCallback(async () => {
    try {
      const res = await axios.get("/admin/routes");
      setRoutes(Array.isArray(res.data) ? res.data : []);
    } catch { setRoutes([]); }
  }, []);

  const loadStarts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get("/admin/starts");
      setStarts(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      showFlash(e.response?.data?.message ?? "始発地の取得に失敗しました", "error");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepots();
    loadRoutes();
    loadStarts();
  }, [loadDepots, loadRoutes, loadStarts]);

  function showFlash(message, type = "success") {
    setFlash({ message, type });
  }

  // ── フィルター操作 ──
  function handleDepotFilterChange(v) { setDepotFilter(v); setRouteFilter(""); setCurrentPage(1); }
  function handleRouteFilterChange(v) { setRouteFilter(v); setCurrentPage(1); }
  function handleSearchChange(v)      { setSearchQ(v); setCurrentPage(1); }

  // フィルタUI用路線リスト
  const filteredRoutesForFilter = depotFilter
    ? routes.filter((r) => String(r.depot_id) === String(depotFilter))
    : routes;

  // ── フィルタリング・ページネーション ──
  const filtered = starts.filter((s) => {
    const q = searchQ.toLowerCase();
    const matchQ = !searchQ || s.short_name.toLowerCase().includes(q) || s.full_name.toLowerCase().includes(q);
    const matchD = !depotFilter || (() => {
      const route = routes.find((r) => r.id === s.route_id);
      return route && String(route.depot_id) === String(depotFilter);
    })();
    const matchR = !routeFilter || String(s.route_id) === String(routeFilter);
    return matchQ && matchD && matchR;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const safePage   = Math.min(currentPage, totalPages);
  const pageItems  = filtered.slice((safePage - 1) * PER_PAGE, safePage * PER_PAGE);

  // モーダル用路線リスト（営業所選択後に絞り込み）
  const routesForForm = form.depot_id
    ? routes.filter((r) => String(r.depot_id) === String(form.depot_id))
    : routes;

  function handleFormDepotChange(v) {
    setForm((f) => ({ ...f, depot_id: v, route_id: "" }));
  }

  // ── モーダル操作 ──
  function openAdd() {
    setEditStart(null);
    setForm({ depot_id: "", route_id: "", short_name: "", full_name: "", sort_number: "" });
    setModalOpen(true);
  }

  function openEdit(start) {
    const route = routes.find((r) => r.id === start.route_id);
    setEditStart(start);
    setForm({
      depot_id:    route ? String(route.depot_id) : "",
      route_id:    String(start.route_id),
      short_name:  start.short_name,
      full_name:   start.full_name,
      sort_number: String(start.sort_number ?? 100),
    });
    setModalOpen(true);
  }

  function closeModal() { setModalOpen(false); setEditStart(null); }

  async function handleSave() {
    if (!form.depot_id)          { showFlash("営業所は必須です", "error"); return; }
    if (!form.route_id)          { showFlash("路線は必須です", "error"); return; }
    if (!form.short_name.trim()) { showFlash("短縮名は必須です", "error"); return; }
    if (!form.full_name.trim())  { showFlash("正式名は必須です", "error"); return; }

    setSaving(true);
    const payload = {
      route_id:   Number(form.route_id),
      short_name: form.short_name.trim(),
      full_name:  form.full_name.trim(),
      ...(form.sort_number !== "" && { sort_number: Number(form.sort_number) }),
    };

    try {
      if (editStart) {
        await axios.put(`/admin/starts/${editStart.id}`, payload);
        setStarts((prev) =>
          prev.map((s) =>
            s.id === editStart.id
              ? {
                  ...s, ...payload,
                  route_short_name: routes.find((r) => r.id === payload.route_id)?.short_name ?? s.route_short_name,
                  route_full_name:  routes.find((r) => r.id === payload.route_id)?.full_name  ?? s.route_full_name,
                }
              : s
          )
        );
        showFlash("更新しました");
      } else {
        const res = await axios.post("/admin/starts", payload);
        setStarts((prev) => [res.data, ...prev]);
        showFlash("追加しました");
      }
      closeModal();
    } catch (e) {
      const errors = e.response?.data?.errors;
      if (errors) {
        showFlash(Object.values(errors).flat()[0], "error");
      } else {
        showFlash(e.response?.data?.message ?? "保存に失敗しました", "error");
      }
    } finally {
      setSaving(false);
    }
  }

  function getDepotShortName(routeId) {
    const route = routes.find((r) => r.id === routeId);
    if (!route) return "";
    return depots.find((d) => d.id === route.depot_id)?.short_name ?? "";
  }

  // ── Render ──
  return (
    <div className="adm-page">
      <AdminBreadcrumb items={[{ label: "始発地管理" }]} />

      <Flash message={flash.message} type={flash.type} onClose={() => setFlash({ ...flash, message: "" })} />

      <div className="adm-header">
        <div className="adm-header-left">
          <h2 className="adm-title">始発地管理</h2>
          <span className="adm-badge adm-badge--teal">admin</span>
        </div>
        <button className="adm-btn adm-btn--primary" onClick={openAdd}>
          ＋ 始発地を追加
        </button>
      </div>

      {/* フィルター */}
      <div className="adm-filter-box">
        <div className="adm-filter-row">
          <div className="adm-filter-field">
            <span className="adm-filter-label">検索</span>
            <input
              type="text"
              className="adm-input"
              placeholder="短縮名・正式名"
              value={searchQ}
              onChange={(e) => handleSearchChange(e.target.value)}
              style={{ minWidth: 170 }}
            />
          </div>
          <div className="adm-filter-field">
            <span className="adm-filter-label">営業所</span>
            <select
              className="adm-select"
              value={depotFilter}
              onChange={(e) => handleDepotFilterChange(e.target.value)}
              style={{ minWidth: 120 }}
            >
              <option value="">全営業所</option>
              {depots.map((d) => (
                <option key={d.id} value={d.id}>{d.short_name}</option>
              ))}
            </select>
          </div>
          <div className="adm-filter-field">
            <span className="adm-filter-label">路線</span>
            <select
              className="adm-select"
              value={routeFilter}
              onChange={(e) => handleRouteFilterChange(e.target.value)}
              style={{ minWidth: 170 }}
            >
              <option value="">全路線</option>
              {filteredRoutesForFilter.map((r) => (
                <option key={r.id} value={r.id}>[{r.short_name}] {r.full_name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* テーブル */}
      <div className="adm-card">
        <div className="adm-card__header">
          始発地一覧
          <span className="adm-card__header-sub">{filtered.length} 件</span>
        </div>
        <div className="adm-table-wrap">
          <table className="adm-table">
            <thead>
              <tr>
                {["ID", "営業所", "路線", "短縮名", "正式名", "表示順", "操作"].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="adm-empty">読み込み中…</td></tr>
              ) : pageItems.length === 0 ? (
                <tr><td colSpan={7} className="adm-empty">該当する始発地がありません</td></tr>
              ) : (
                pageItems.map((start) => (
                  <tr key={start.id}>
                    <td className="adm-table__id">{start.id}</td>
                    <td>
                      <span className="adm-badge adm-badge--blue">
                        {getDepotShortName(start.route_id)}
                      </span>
                    </td>
                    <td className="adm-table__mute">
                      <span className="adm-table__bold">{start.route_short_name}</span>
                      <span style={{ color: "var(--adm-text-muted)", marginLeft: 4 }}>
                        {start.route_full_name}
                      </span>
                    </td>
                    <td className="adm-table__bold">{start.short_name}</td>
                    <td className="adm-table__mute">{start.full_name}</td>
                    <td className="adm-center adm-table__mute">
                      {start.sort_number === 9999 ? (
                        <span style={{ color: "var(--adm-text-muted)" }}>9999</span>
                      ) : start.sort_number}
                    </td>
                    <td>
                      <button
                        className="adm-btn adm-btn--default adm-btn--sm"
                        onClick={() => openEdit(start)}
                      >
                        編集
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          current={safePage}
          total={totalPages}
          onChange={setCurrentPage}
          count={filtered.length}
        />
      </div>

      {/* 追加・編集モーダル */}
      <Modal show={modalOpen} title={editStart ? "始発地を編集" : "始発地を追加"} onClose={closeModal}>
        <FormField label="営業所" required>
          <select
            className="adm-select adm-select--full"
            value={form.depot_id}
            onChange={(e) => handleFormDepotChange(e.target.value)}
          >
            <option value="">選択してください</option>
            {depots.map((d) => (
              <option key={d.id} value={d.id}>{d.short_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="路線" required>
          <select
            className="adm-select adm-select--full"
            value={form.route_id}
            onChange={(e) => setForm((f) => ({ ...f, route_id: e.target.value }))}
            disabled={!form.depot_id}
            style={{ opacity: form.depot_id ? 1 : 0.5, cursor: form.depot_id ? "pointer" : "not-allowed" }}
          >
            <option value="">{form.depot_id ? "選択してください" : "先に営業所を選択"}</option>
            {routesForForm.map((r) => (
              <option key={r.id} value={r.id}>[{r.short_name}] {r.full_name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="短縮名" required>
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.short_name}
            onChange={(e) => setForm((f) => ({ ...f, short_name: e.target.value }))}
            placeholder="例: 天神"
            maxLength={50}
          />
        </FormField>
        <FormField label="正式名" required>
          <input
            type="text"
            className="adm-input adm-input--full"
            value={form.full_name}
            onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            placeholder="例: 天神バスターミナル"
            maxLength={100}
          />
        </FormField>
        <FormField label="表示順" required hint="9999 を設定すると一般画面に表示されません">
          <div className="adm-sort-row">
            <button
              type="button"
              className="adm-btn adm-btn--default adm-btn--sm"
              onClick={() => setForm((f) => ({ ...f, sort_number: String(Math.max(0, Number(f.sort_number) - 10)) }))}
            >−10</button>
            <input
              type="number"
              className="adm-input adm-input--full"
              value={form.sort_number}
              onChange={(e) => setForm((f) => ({ ...f, sort_number: e.target.value }))}
              placeholder="100"
              min={0} max={9999}
              style={{ textAlign: "center" }}
            />
            <button
              type="button"
              className="adm-btn adm-btn--default adm-btn--sm"
              onClick={() => setForm((f) => ({ ...f, sort_number: String(Math.min(9999, Number(f.sort_number) + 10)) }))}
            >＋10</button>
          </div>
        </FormField>
        <ModalActions
          onCancel={closeModal}
          onConfirm={handleSave}
          confirmLabel={saving ? "保存中…" : "保存"}
          disabled={saving}
        />
      </Modal>
    </div>
  );
}
